import posthog from 'posthog-js';
import processProxy from '@/env/processProxy';
import VueRouter, { Route } from 'vue-router';
import ClientAuthService from '@/modules/client/services/ClientAuthService';
import AuthService from '@/modules/user/AuthService';
import { Routes } from '@/router/Routes';

const CLIENT_PANEL_PAGE_VIEW_EVENT = 'client_panel_page_view';
const CLIENT_PANEL_ROOT_PATH = '/client';
// Calculator routes nest under product type segments such as
// `productTypeMortgage` / `productTypeCash` (see CalculatorRoutes.ts) — any
// path containing that substring is the advisor calculator.
const CALCULATOR_ROUTE_KEYWORD = 'productType';
const MULTIFORM_ROUTE_NAMES = new Set([
  Routes.CLIENT_USER.MULTIFORM,
  Routes.CLIENT_USER.RESTRICTED_MULTIFORM,
]);

let isPosthogInitialized = false;
let identifiedClientId: Nullable<number> = null;
let identifiedUserId: Nullable<number> = null;
let isClientPanelTrackingScopeActive = false;
let isCalculatorTrackingScopeActive = false;
let isAdvisorMultiformTrackingScopeActive = false;
let isPosthogCapturingEnabled = false;

const SAFE_EVENT_PROPERTIES = new Set([
  'routeName',
  'routeTemplate',
  'panelMode',
  'isClientPanel',
  'status',
  'result',
  'errorCode',
  'flow',
  'step',
  'action',
  'source',
  'section',
  'type',
  'isRestricted',
  'hasTeleconference',
  'hasNote',
  'noteLength',
  'documentsCollectedCount',
  'selectedOffersCount',
  'notificationsCount',
  'hasAcceptedRegulations',
]);

export const hasPosthogApiKey = (): boolean => {
  return !!processProxy.VUE_APP_POSTHOG_KEY?.trim();
};

const isClientPanelRoute = (route: Route): boolean => {
  return route.path === CLIENT_PANEL_ROOT_PATH || route.path.startsWith(`${CLIENT_PANEL_ROOT_PATH}/`);
};

const isCalculatorRoute = (route: Route): boolean => {
  // Detect across path, route name AND matched templates — not just `path`.
  // The comparator results view (`*-summary`) resolves to path `/` when its
  // `:simulationId` param is absent (e.g. a freshly-created or failed sim),
  // so `path` alone misses it; but `route.name` (`productTypeMortgage-summary`)
  // and the matched template (`/app/productTypeMortgage/calculator-summary/...`)
  // still carry the keyword. Without this, capturing gets opted out on the
  // results view and every comparator_v25_* event is dropped.
  if (route.path.includes(CALCULATOR_ROUTE_KEYWORD)) {
    return true;
  }
  if (typeof route.name === 'string' && route.name.includes(CALCULATOR_ROUTE_KEYWORD)) {
    return true;
  }
  return route.matched.some(record => record.path.includes(CALCULATOR_ROUTE_KEYWORD));
};

const isClientMultiformRoute = (route: Route): boolean => {
  const routeName = typeof route.name === 'string' ? route.name : '';
  if (MULTIFORM_ROUTE_NAMES.has(routeName)) {
    return true;
  }

  if (route.path.includes('/multiform')) {
    return true;
  }

  return route.matched.some(record => record.path.includes('multiform'));
};

const isAdvisorMultiformRoute = (route: Route): boolean => {
  // The advisor multiwniosek lives in the deal-details subtree: the deal
  // preview (share toggle), the borrowers / clientData / creditDetails /
  // realEstates wizard steps, and document download. The DEAL_DETAILS record
  // carries this name and is present in `matched` for the index route and all
  // its children, so a single check covers the whole flow.
  if (route.name === Routes.DEALS.DEAL_DETAILS) {
    return true;
  }
  return route.matched.some(record => record.name === Routes.DEALS.DEAL_DETAILS);
};

const getSafeRouteTemplate = (route: Route): string => {
  const templates = route.matched
    .map(record => record.path)
    .filter(path => !!path && path !== '/');

  return templates.join(' > ') || route.name?.toString() || 'unknown';
};

const getClientPanelMode = (route: Route): 'authenticated' | 'restricted' => {
  return route.path.includes('/restricted/') ? 'restricted' : 'authenticated';
};

const sanitizeAutocaptureProperties = (properties: Record<string, unknown>): Record<string, unknown> => {
  const sanitizedProperties = { ...properties };
  delete sanitizedProperties.$referrer;
  delete sanitizedProperties.$referring_domain;
  return sanitizedProperties;
};

const initPosthogIfNeeded = (): void => {
  if (isPosthogInitialized || !hasPosthogApiKey()) {
    return;
  }

  posthog.init(processProxy.VUE_APP_POSTHOG_KEY!.trim(), {
    api_host: 'https://eu.i.posthog.com',
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: false,
    disable_persistence: true,
    persistence: 'memory',
    sanitize_properties: sanitizeAutocaptureProperties,
    // PII hardening: the advisor scopes (calculator + multiwniosek) opt
    // capturing IN on routes that display client personal / financial data.
    // Autocapture would otherwise send each clicked element's textContent and
    // attributes (incl. detached select-dropdown option labels that fall
    // outside any `ph-no-capture` subtree). Mask both so autocapture keeps
    // structural click signal but never the text/attribute values. Explicit
    // `capture()` events are unaffected — their PII safety is the per-module
    // allowlist (e.g. SAFE_MULTIFORM_PROPERTIES).
    mask_all_text: true,
    mask_all_element_attributes: true,
    session_recording: {
      maskAllInputs: true,
    },
  } as any);

  isPosthogInitialized = true;
};

const setPosthogCaptureState = (isEnabled: boolean): void => {
  if (!isPosthogInitialized || isPosthogCapturingEnabled === isEnabled) {
    return;
  }

  if (isEnabled) {
    posthog.opt_in_capturing();
  } else {
    posthog.opt_out_capturing();
  }

  isPosthogCapturingEnabled = isEnabled;
};

const resetIdentityIfNeeded = (): void => {
  if (!isPosthogInitialized || identifiedClientId === null) {
    return;
  }

  posthog.reset();
  identifiedClientId = null;
};

const identifyClientIfNeeded = (clientId: number): void => {
  if (identifiedClientId === clientId) {
    return;
  }

  posthog.identify(String(clientId), {
    clientId,
  });

  identifiedClientId = clientId;
};

const resetUserIdentityIfNeeded = (): void => {
  if (!isPosthogInitialized || identifiedUserId === null) {
    return;
  }

  posthog.reset();
  identifiedUserId = null;
};

const identifyUserIfNeeded = (userId: number): void => {
  if (identifiedUserId === userId) {
    return;
  }

  // Prefix avoids colliding with `clientId`-based identities in the client
  // panel scope (both are numeric ids drawn from different domains).
  posthog.identify(`user-${userId}`, {
    userId,
  });

  identifiedUserId = userId;
};

const shouldTrackClientPanelRoute = (route: Route): boolean => {
  if (!isClientPanelRoute(route)) {
    return false;
  }

  if (isClientMultiformRoute(route)) {
    return false;
  }

  return true;
};

const syncPosthogWithRoute = (route: Route): void => {
  if (!hasPosthogApiKey()) {
    return;
  }

  isClientPanelTrackingScopeActive = shouldTrackClientPanelRoute(route);
  isCalculatorTrackingScopeActive = !isClientPanelTrackingScopeActive && isCalculatorRoute(route);
  isAdvisorMultiformTrackingScopeActive =
    !isClientPanelTrackingScopeActive && !isCalculatorTrackingScopeActive && isAdvisorMultiformRoute(route);

  if (!isClientPanelTrackingScopeActive && !isCalculatorTrackingScopeActive && !isAdvisorMultiformTrackingScopeActive) {
    resetIdentityIfNeeded();
    resetUserIdentityIfNeeded();
    if (isPosthogInitialized) {
      setPosthogCaptureState(false);
    }
    return;
  }

  initPosthogIfNeeded();
  setPosthogCaptureState(true);

  if (isClientPanelTrackingScopeActive) {
    resetUserIdentityIfNeeded();
    const clientId = ClientAuthService.User?.id;
    const isClientAuthenticated = ClientAuthService.isTokenValid && !!clientId;

    if (!isClientAuthenticated) {
      resetIdentityIfNeeded();
      return;
    }

    identifyClientIfNeeded(clientId);
    captureClientPanelEvent(CLIENT_PANEL_PAGE_VIEW_EVENT, {
      routeName: route.name?.toString() || 'unknown',
      routeTemplate: getSafeRouteTemplate(route),
      panelMode: getClientPanelMode(route),
      isClientPanel: true,
    });
    return;
  }

  // Advisor scopes (calculator + multiwniosek): identify by the employee's
  // User.id when authenticated. No explicit page_view event — posthog
  // autocapture's $pageview covers it.
  resetIdentityIfNeeded();
  const userId = AuthService.User?.id;
  if (typeof userId !== 'number') {
    resetUserIdentityIfNeeded();
    return;
  }
  identifyUserIfNeeded(userId);
};

const toSafePrimitiveValue = (value: unknown): string | number | boolean | null => {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (value === null) {
    return null;
  }

  return String(value);
};

const toSafeEventProperties = (properties: Record<string, unknown>): Record<string, string | number | boolean | null> => {
  const safeProperties: Record<string, string | number | boolean | null> = {};

  if (Object.prototype.hasOwnProperty.call(properties, 'teleconferenceUrl')) {
    safeProperties.hasTeleconference = !!properties.teleconferenceUrl;
  }

  if (Object.prototype.hasOwnProperty.call(properties, 'noteText') || Object.prototype.hasOwnProperty.call(properties, 'note')) {
    const noteTextCandidate = properties.noteText ?? properties.note;
    const noteText = typeof noteTextCandidate === 'string' ? noteTextCandidate : '';
    safeProperties.hasNote = noteText.trim().length > 0;
    safeProperties.noteLength = noteText.length;
  }

  Object.entries(properties).forEach(([key, value]) => {
    if (!SAFE_EVENT_PROPERTIES.has(key)) {
      return;
    }

    safeProperties[key] = toSafePrimitiveValue(value);
  });

  return safeProperties;
};

export const captureClientPanelEvent = (
  eventName: string,
  properties: Record<string, unknown> = {},
): void => {
  if (!isPosthogInitialized || !isClientPanelTrackingScopeActive || !hasPosthogApiKey()) {
    return;
  }

  posthog.capture(eventName, toSafeEventProperties(properties));
};

export const setupClientPanelPosthogTracking = (router: VueRouter): void => {
  if (!hasPosthogApiKey()) {
    return;
  }

  router.afterEach(to => {
    syncPosthogWithRoute(to);
  });

  syncPosthogWithRoute(router.currentRoute);
};

/**
 * Defensive late-identify hook for the calculator scope.
 *
 * The route guard only checks token validity (`isAuthenticatedForRoute`), but
 * `AuthService.User` is populated asynchronously by `fetchUserInfo()` — so on
 * a cold reload of a calculator URL the route can resolve (and
 * `syncPosthogWithRoute` fire) BEFORE the user profile lands. In that window
 * the route-driven identify gets skipped.
 *
 * Called from every comparator capture so that the very first event after
 * `User` arrives still attaches the correct `user-<id>` distinct_id. Idempotent
 * — once identified, subsequent calls short-circuit.
 */
export const ensureCalculatorUserIdentified = (): void => {
  if (!isCalculatorTrackingScopeActive || identifiedUserId !== null) {
    return;
  }
  const userId = AuthService.User?.id;
  if (typeof userId === 'number') {
    identifyUserIfNeeded(userId);
  }
};

/**
 * Defensive late-identify hook for the advisor multiwniosek scope. Mirrors
 * `ensureCalculatorUserIdentified` — see its rationale. Called from every
 * multiform capture so the first event after `User` arrives still attaches the
 * correct `user-<id>` distinct_id. Idempotent.
 */
export const ensureMultiformUserIdentified = (): void => {
  if (!isAdvisorMultiformTrackingScopeActive || identifiedUserId !== null) {
    return;
  }
  const userId = AuthService.User?.id;
  if (typeof userId === 'number') {
    identifyUserIfNeeded(userId);
  }
};

export const resetClientPanelPosthogTrackingStateForTests = (): void => {
  isPosthogInitialized = false;
  identifiedClientId = null;
  identifiedUserId = null;
  isClientPanelTrackingScopeActive = false;
  isCalculatorTrackingScopeActive = false;
  isAdvisorMultiformTrackingScopeActive = false;
  isPosthogCapturingEnabled = false;
};
